// client/src/components/marketer/MarketerPointCharge.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PROJECT_CREATION_PAUSED, V2_UPDATE_DATE_TEXT } from '../../config/maintenance';
import '../../styles/marketer/MarketerPointCharge.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const MIN_CHARGE  = 10000;
const MAX_INPUT   = 50000000;
const FEE_RATE    = 0.10;
const POLL_MS     = 5000; // 5초 폴링

// 남은 시간(초) → mm:ss 포맷
function formatCountdown(secsLeft) {
  if (secsLeft <= 0) return '00:00';
  const m = Math.floor(secsLeft / 60);
  const s = secsLeft % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// 토스트 컴포넌트
function Toast({ msg, onClose }) {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [msg, onClose]);
  if (!msg) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
      background: '#065f46', color: '#fff', padding: '14px 28px',
      borderRadius: 10, fontSize: 15, fontWeight: 600,
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)', zIndex: 9999,
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      ✅ {msg}
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
    </div>
  );
}

export default function MarketerPointCharge() {
  const [points, setPoints]       = useState(0);
  const [depositor, setDepositor] = useState('');
  const [loading, setLoading]     = useState(false);
  const [msg, setMsg]             = useState('');
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [toast, setToast]         = useState('');

  // 사용자 키 (sessionStorage 캐시 키 분리용)
  const [userKey, setUserKey] = useState('');
  const [bootingUser, setBootingUser] = useState(true);

  // 진행 중인 요청 (status:'open')
  const [pending, setPending]         = useState([]);
  const [lastCompleted, setLastCompleted] = useState(null);

  // 폴링용 hadOpenItem: 이전 폴링에 open 건이 있었는지 추적
  const hadOpenItemRef = useRef(false);

  // 카운트다운 타이머 상태
  const [secsLeft, setSecsLeft] = useState(0);
  const timerRef = useRef(null);

  const amount = useMemo(() => Math.round(points * (1 + FEE_RATE)), [points]);
  const isBelowMin = points < MIN_CHARGE;
  const hasPending = pending.length > 0;

  // 카운트다운 시작/재설정
  const startCountdown = useCallback((expiredAt) => {
    if (timerRef.current) clearInterval(timerRef.current);
    const calc = () => Math.max(0, Math.floor((new Date(expiredAt) - Date.now()) / 1000));
    setSecsLeft(calc());
    timerRef.current = setInterval(() => {
      const left = calc();
      setSecsLeft(left);
      if (left <= 0) clearInterval(timerRef.current);
    }, 1000);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // pending 변경 시 카운트다운 갱신
  useEffect(() => {
    if (pending.length > 0 && pending[0].expiredAt) {
      startCountdown(pending[0].expiredAt);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      setSecsLeft(0);
    }
  }, [pending, startCountdown]);

  // 사용자 확인 + 초기 pending 로드
  useEffect(() => {
    let alive = true;
    (async () => {
      let uk = '';
      try {
        const meRes = await fetch(`${API_URL}/api/user/me`, { credentials: 'include' });
        const me = await meRes.json().catch(() => ({}));
        if (meRes.ok && me?.ok) uk = String(me.user?.userId || '').toLowerCase();
      } catch {}
      if (!alive) return;

      try { if (uk) sessionStorage.setItem('current_user_key', uk); } catch {}
      setUserKey(uk);

      // 세션 캐시 읽기
      let init = [];
      try {
        const pk = uk ? `mrk_pending__${uk}` : '';
        sessionStorage.removeItem('mrk_pending');
        sessionStorage.removeItem('mrk_pending_cache');
        if (pk) init = JSON.parse(sessionStorage.getItem(pk) || '[]');
      } catch {}
      setPending(init);
      hadOpenItemRef.current = init.length > 0;
      setBootingUser(false);

      // 서버에서 최신 pending 조회
      if (uk) {
        try {
          const r = await fetch(`${API_URL}/api/marketer/mybiz/points/pending-list`, { credentials: 'include' });
          const j = await r.json().catch(() => ({}));
          if (!alive) return;
          if (r.ok && j?.ok) {
            setPending(j.items || []);
            setLastCompleted(j.lastCompletedCharge || null);
            hadOpenItemRef.current = (j.items || []).length > 0;
            try { sessionStorage.setItem(`mrk_pending__${uk}`, JSON.stringify(j.items || [])); } catch {}
          }
        } catch {}
      }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line

  // 폴링: 5초마다 pending 상태 갱신
  useEffect(() => {
    if (bootingUser || !userKey) return;

    const poll = async () => {
      try {
        const r = await fetch(`${API_URL}/api/marketer/mybiz/points/pending-list`, { credentials: 'include' });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !j?.ok) return;

        const newItems = j.items || [];
        const newLast  = j.lastCompletedCharge || null;

        // 충전 완료 감지: 이전 폴링에 open 건이 있었고 → 지금은 없고 → lastCompletedCharge가 새로 있을 때
        if (
          hadOpenItemRef.current &&
          newItems.length === 0 &&
          newLast &&
          (!lastCompleted || newLast.merchantUid !== lastCompleted?.merchantUid)
        ) {
          setToast(`포인트 충전이 완료되었습니다! +${Number(newLast.points).toLocaleString()}P`);
          window.dispatchEvent(new CustomEvent('points-changed'));
        }

        hadOpenItemRef.current = newItems.length > 0;
        setPending(newItems);
        setLastCompleted(newLast);
        try { sessionStorage.setItem(`mrk_pending__${userKey}`, JSON.stringify(newItems)); } catch {}
      } catch {}
    };

    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [bootingUser, userKey, lastCompleted]);

  const clamp = (v) => Math.max(0, Math.min(MAX_INPUT, v));
  const inc   = (v) => setPoints(p => clamp(p + v));
  const onChangePoints = (e) => {
    const v = Number(String(e.target.value).replace(/\D/g, '') || 0);
    setPoints(clamp(v));
  };

  // 충전 요청
  const handleRequest = async () => {
    setMsg('');
    if (isBelowMin)          { setMsg('10,000포인트부터 충전 가능합니다.'); return; }
    if (!depositor.trim())   { setMsg('입금자명을 입력해주세요.'); return; }
    if (!agreementChecked)   { setMsg('약관에 동의해주세요.'); return; }
    if (hasPending)          { setMsg('이미 진행 중인 요청이 있습니다. 취소 후 새로 요청해주세요.'); return; }

    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/marketer/mybiz/points/manual-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ points, depositor: depositor.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) throw new Error(j?.message || '요청 실패');

      // 요청 성공 → pending 상태 즉시 반영
      const newItem = {
        merchantUid: j.merchantUid,
        amount:      j.amount,
        points,
        payMethod:   'bank',
        bankDepositor: depositor.trim(),
        expiredAt:   j.expiredAt,
        bank:        j.bank,
        createdAt:   new Date().toISOString(),
      };
      setPending([newItem]);
      hadOpenItemRef.current = true;
      try { sessionStorage.setItem(`mrk_pending__${userKey}`, JSON.stringify([newItem])); } catch {}

      // 입력 초기화
      setPoints(0);
      setDepositor('');
      setAgreementChecked(false);
    } catch (e) {
      setMsg(e.message || '요청 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 요청 취소
  const handleCancel = async (merchantUid) => {
    if (!window.confirm('입금 요청을 취소하시겠습니까?')) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/marketer/mybiz/points/pending/${merchantUid}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) throw new Error(j?.message || '취소 실패');

      setPending([]);
      hadOpenItemRef.current = false;
      try { sessionStorage.setItem(`mrk_pending__${userKey}`, JSON.stringify([])); } catch {}
    } catch (e) {
      setMsg(e.message || '취소 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const disableRequestBtn = loading || isBelowMin || !depositor.trim() || !agreementChecked || hasPending;

  // 진행 중인 요청의 bank 정보 (API 응답 또는 fallback)
  const activePending = pending[0] || null;
  const bankInfo = activePending?.bank || {};

  return (
    <div className="mb-card mb-card--full-height">
      <div className="mb-page-header">
        <h2 className="mb-page-title">포인트 충전</h2>
        <p className="mb-page-description">포인트를 충전하여 프로젝트를 진행하세요.</p>
      </div>

      {/* v2 전환: 생성 중단 기간 충전 고지 (10/10 배포 시 제거) */}
      {PROJECT_CREATION_PAUSED && (
        <div className="note note--amber" style={{ marginBottom: 16 }}>
          {V2_UPDATE_DATE_TEXT} 대규모 업데이트 준비로 <b>신규 프로젝트 생성이 일시 중단</b>되어,
          지금 충전한 포인트는 업데이트 이후부터 사용하실 수 있어요.
          충전된 포인트는 업데이트 후에도 그대로 유지됩니다.
        </div>
      )}

      {/* 진행 중인 요청 카드 */}
      {!bootingUser && activePending && (
        <div style={{
          background: '#f7faff', borderRadius: 16, padding: '20px 22px', marginBottom: 24,
          boxShadow: 'inset 0 0 0 1.5px #2f6bff, 0 1px 2px rgba(11,19,36,.04)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <span className="mb-badge mb-badge-blue mb-badge--dot">입금 대기 중</span>
              <dl style={{ margin: '12px 0 0' }}>
                <div className="kv"><dt>요청 포인트</dt><dd>{Number(activePending.points).toLocaleString()}P</dd></div>
                <div className="kv"><dt>입금 금액 (VAT 포함)</dt><dd>{Number(activePending.amount).toLocaleString()}원</dd></div>
                <div className="kv"><dt>입금자명</dt><dd>{activePending.bankDepositor || '-'}</dd></div>
                {bankInfo.name && (
                  <div className="kv"><dt>입금 계좌</dt><dd>{bankInfo.name} {bankInfo.number} ({bankInfo.holder})</dd></div>
                )}
              </dl>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="t-sub">남은 시간</div>
              <div style={{
                fontSize: 26, fontWeight: 800,
                color: secsLeft > 300 ? '#2f6bff' : '#e5484d',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {formatCountdown(secsLeft)}
              </div>
              <button
                type="button"
                className="mb-btn mb-btn-danger mb-btn-sm"
                onClick={() => handleCancel(activePending.merchantUid)}
                disabled={loading}
                style={{ marginTop: 10 }}
              >
                요청 취소
              </button>
            </div>
          </div>
          <div className="note note--amber">
            반드시 <strong>30분 이내</strong>에 입금해주세요. 입금자명이 다르면 자동 확인이 되지 않습니다.
          </div>
        </div>
      )}

      <div className="mb-grid-2" style={{ alignItems: 'stretch' }}>
        {/* 왼쪽: 충전 금액 */}
        <div style={{ border: '1px solid #eceef2', borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ fontSize: 15.5, fontWeight: 800, margin: '0 0 14px', color: '#0b1324' }}>충전 금액</h3>
          <div className="mb-info-field">
            <label className="mb-info-label">충전 포인트</label>
            <input
              value={points}
              onChange={onChangePoints}
              disabled={loading || hasPending}
              className="mb-form-control"
              placeholder="10,000 ~ 50,000,000"
            />
            {isBelowMin && points > 0 ? (
              <p className="mb-info-hint" style={{ color: '#e5484d' }}>10,000포인트부터 충전 가능합니다.</p>
            ) : (
              <p className="mb-info-hint">10,000포인트부터 충전할 수 있습니다.</p>
            )}
          </div>

          <div className="chips" style={{ marginBottom: 16 }}>
            {[10000, 100000, 500000, 1000000].map(v => (
              <button
                key={v}
                type="button"
                className="chip"
                onClick={() => inc(v)}
                disabled={loading || hasPending}
              >
                +{(v / 10000) < 100 ? `${v / 10000}만` : `${v / 10000 / 100}백만`}
                <span style={{ fontSize: 11, fontWeight: 500, color: '#6b7686', marginLeft: 5 }}>
                  {Math.round(v * 1.1).toLocaleString()}원
                </span>
              </button>
            ))}
          </div>

          <div style={{ background: '#f6f8fb', borderRadius: 14, padding: '6px 16px', marginTop: 'auto' }}>
            <dl style={{ margin: 0 }}>
              <div className="kv"><dt>충전 포인트</dt><dd>{points.toLocaleString()}P</dd></div>
              <div className="kv"><dt>부가세 (VAT 10%)</dt><dd>+{(amount - points).toLocaleString()}원</dd></div>
              <div className="kv">
                <dt style={{ color: '#0b1324', fontWeight: 800 }}>최종 결제 금액</dt>
                <dd style={{ color: '#2f6bff', fontSize: 17 }}>{amount.toLocaleString()}원</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* 오른쪽: 입금 정보 */}
        <div style={{ border: '1px solid #eceef2', borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ fontSize: 15.5, fontWeight: 800, margin: '0 0 14px', color: '#0b1324' }}>입금 정보</h3>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            border: '1.5px solid #2f6bff', background: '#eef3ff', borderRadius: 14,
            padding: '13px 16px', marginBottom: 14, fontWeight: 700, fontSize: 14.5, color: '#1f4fd6',
          }}>
            무통장 입금 <span style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 700 }}>선택됨 ✓</span>
          </div>

          <div className="mb-info-field">
            <label className="mb-info-label">입금자명</label>
            <input
              value={depositor}
              onChange={(e) => setDepositor(e.target.value)}
              placeholder="이체 시 표시되는 이름을 정확히 입력하세요 (예: 홍길동)"
              disabled={loading || hasPending}
              className="mb-form-control"
            />
            <p className="mb-info-hint">실제 이체 시 표시되는 이름과 반드시 일치해야 합니다.</p>
          </div>

          <div className="note note--amber" style={{ marginTop: 0, marginBottom: 14 }}>
            반드시 <strong>30분 이내</strong>에 입금해주세요. 입금자명이 다르면 자동 확인이 되지 않습니다.
          </div>

          {/* 약관 동의 */}
          <div className="payment-agreement">
            <label className="payment-agreement__label">
              <input
                type="checkbox"
                checked={agreementChecked}
                onChange={(e) => setAgreementChecked(e.target.checked)}
                disabled={loading || hasPending}
                className="payment-agreement__checkbox"
              />
              <span className="payment-agreement__text">
                <a href="/terms" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>서비스 이용약관</a>
                {', '}
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>개인정보 처리방침</a>
                {', '}
                <a href="/refund-policy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>환불정책</a>
                을 확인했으며 결제에 동의합니다. (필수)
              </span>
            </label>
          </div>

          {hasPending && (
            <div className="note note--amber" style={{ marginTop: 0, marginBottom: 10 }}>
              진행 중인 요청이 있습니다. 취소 후 새로 요청해주세요.
            </div>
          )}

          <button
            type="button"
            onClick={handleRequest}
            disabled={disableRequestBtn}
            className="mb-btn mb-btn-primary mb-btn-full mb-btn-lg"
            style={{ marginTop: 'auto' }}
          >
            {loading ? '처리 중...' : '충전 요청하기'}
          </button>

          {/* 사업자 정보 */}
          <p style={{ margin: '10px 0 0', fontSize: 12, color: '#6b7686', lineHeight: 1.6 }}>
            (주)카보 · 대표 이동진 · 사업자등록번호 291-86-02840 · 충청남도 계룡시 장안1길 6, 202호 · 전화 044-715-7442
          </p>
        </div>
      </div>

      {msg && (
        <div className="note note--red" style={{ marginTop: 16 }}>
          {msg}
        </div>
      )}

      <Toast msg={toast} onClose={() => setToast('')} />
    </div>
  );
}
